import { forwardRef, useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import {
  Stack,
  Button,
  Grid,
  FormControl,
  InputLabel,
  OutlinedInput,
  FormControlLabel,
  Switch,
  MenuItem,
  Select,
} from "@mui/material";
import FormSection from "../../Forms/FormSection/form-section";
import { setupService, userService, workflowService } from "../../../Core/Api/services";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import { DbWorkflowDefinitionInfos } from "../../../Core/Api/Types/db-workflow-definition-infos";
import { WorkflowType } from "../../../Core/Api/Types/workflow-type";
import { WorkflowFamily } from "../../../Core/Api/Types/workflow-family";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import { DbStorageInfos } from "../../../Core/Api/Types/db-storage-infos";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { produce } from "immer";
import { DbWorkflowDefinitionInputFilterInfos } from "../../../Core/Api/Types/db-workflow-definition-input-filter-infos";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { FileCategory } from "../../../Core/Api/Types/file-category";
import { useTranslation } from "react-i18next";
import { WorkflowEngine } from "../../../Core/Api/Types/workflow-engine";
import FormInputSelect from "../../Forms/FormInputSelect/form-input-select";
import { DbKalikoWorkflowModelInfos } from "../../../Core/Api/Types/kaliko";

export type ModalWorkflowModelEditElementProps = {
  wf: DbWorkflowDefinitionInfos;
  isCreation: boolean;
  feedbackData: (wf: DbWorkflowDefinitionInfos) => void;
};

export type ModalWorkflowModelEditProps = CommonModalProps<ModalWorkflowModelEditElementProps>;

const ModalWorkflowModelEdit = forwardRef<any, ModalWorkflowModelEditProps>((props, ref) => {
  const { t } = useTranslation();
  const { isCreation, closeModal, feedbackData } = props;
  const [wf, setWF] = useState<DbWorkflowDefinitionInfos>(props.wf);
  const [users, setUsers] = useState<DbUserBasicInfos[]>([]);
  const [storages, setStorages] = useState<DbStorageInfos[]>([]);
  const [kalikoWorkflows, setKalikoWorkflows] = useState<DbKalikoWorkflowModelInfos[]>([]);
  const enqueueSnackbar = useCustomSnackbar();

  const handleCancelClick = () => {
    closeModal();
  };

  const handleConfirmClick = () => {
    (isCreation ? workflowService.CreateDefinition(wf) : workflowService.UpdateDefinition(wf)).then((data) => {
      if (!data.success || !data.objectId) {
        enqueueSnackbar(data.message!, "error");
        return;
      }
      feedbackData({ ...wf, id: data.objectId });
      enqueueSnackbar(wf.name!, "success");
      closeModal();
    });
  };

  useEffect(() => {
    userService.GetAllUsersBasic().then((data) => {
      if (data.users) setUsers(data.users ?? []);
    });

    setupService.GetAllStorages().then((data) => {
      if (data.storages) setStorages(data.storages ?? []);
    });

    workflowService.GetWorkflowModels().then((data) => {
      if (data) setKalikoWorkflows(data);
    });
  }, []);

  return (
    <Modal
      title={`${t("modal:title.workflowModel")} ${isCreation ? t("common:creation") : t("common:edit")}`}
      reference={ref}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button variant="contained" color="primary" onClick={handleConfirmClick} disabled={false}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <FormSection title={t("common:generalInformations")} status="todo" defaultExpanded>
        <Grid container spacing={3} flexDirection={"row"}>
          <Grid item md={6}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="name">
                {t("common:name")}
              </InputLabel>
              <OutlinedInput
                value={wf.name ?? ""}
                id="name"
                onChange={(e) => {
                  setWF({ ...wf, name: e.target.value });
                }}
              />
            </FormControl>
          </Grid>
          {wf.engine !== WorkflowEngine.Kaliko ? (
            <Grid item md={6}>
              <FormControl sx={{ width: "100%" }}>
                <InputLabel shrink htmlFor="name2">
                  {t("field:label.workflowEngineName")}
                </InputLabel>
                <OutlinedInput
                  value={wf.daletWorkflowName ?? ""}
                  id="name2"
                  onChange={(e) => {
                    setWF({ ...wf, daletWorkflowName: e.target.value });
                  }}
                />
              </FormControl>
            </Grid>
          ) : (
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("common:kalikoWorkflow")}</InputLabel>
              <Select
                sx={{ width: "100%" }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                fullWidth
                onChange={(e) => {
                  setWF({ ...wf, daletWorkflowName: e.target.value as string });
                }}
              >
                {kalikoWorkflows.map((w) => (
                  <MenuItem value={w.id} key={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          )}
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("common:family")}</InputLabel>
            <Select
              value={wf?.family ?? WorkflowFamily.Generic}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setWF({
                  ...wf,
                  family: e.target.value ? WorkflowFamily[e.target.value] : WorkflowFamily.Generic,
                });
              }}
            >
              {Object.keys(WorkflowFamily).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("common:type")}</InputLabel>
            <Select
              value={wf?.type ?? WorkflowType.Generic}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setWF({
                  ...wf,
                  type: e.target.value ? WorkflowType[e.target.value] : WorkflowType.Generic,
                });
              }}
            >
              {Object.keys(WorkflowType).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setWF({
                      ...wf,
                      isPlayableManually: checked,
                    });
                  }}
                  checked={wf.isPlayableManually}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.isPlayableManually")}
            />
          </Grid>
          <Grid item md={6}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  onChange={(e, checked) => {
                    setWF({
                      ...wf,
                      isAsync: checked,
                    });
                  }}
                  checked={wf.isAsync}
                />
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
              label={t("field:label.isAsync")}
            />
          </Grid>
          <Grid item md={6}>
            <InputLabel htmlFor="lic" sx={{ mb: 1 }}>
              {t("field:label.licencesSlots")}
            </InputLabel>
            <OutlinedInput
              id="lic"
              type={"number"}
              value={wf.licenseSlots ?? 0}
              fullWidth
              onChange={(e) => {
                setWF({
                  ...wf,
                  licenseSlots: e.target.value ? parseInt(e.target.value) : null,
                });
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.outputStorage")}
              options={storages}
              placeholder={t("field:placeholder.selectAStorage")}
              value={storages.find((x) => x.id === wf?.outputStorageId)}
              onChange={(e) => {
                setWF({
                  ...wf,
                  outputStorageId: e ? e.id : null,
                });
              }}
              getOptionLabel={(value: any) => value.name}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "sts",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={12}>
            <FormAutocomplete
              label={t("field:label.apiAuthenticationUser")}
              options={users}
              placeholder={t("field:placeholder.selectReferenceUser")}
              value={users.find((x) => x.id === wf?.userIdReferenceApi)}
              onChange={(e) => {
                setWF({
                  ...wf,
                  userIdReferenceApi: e ? e.id : null,
                });
              }}
              getOptionLabel={(value: any) => value.name + " (#" + value.id + ")"}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "refu",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={12}>
            <FormInputSelect
              label={t("field:label.engine")}
              stackProps={{}}
              fullWidth
              value={wf.engine ?? "none"}
              onChange={(e) => {
                setWF({ ...wf, engine: e.target.value === "none" ? null : (e.target.value as WorkflowEngine) });
              }}
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "engine",
                required: false,
                initialState: false,
              }}
            >
              <MenuItem value={"none"} key={"none"}>
                {t("menu:item.noneHyphen")}
              </MenuItem>
              {Object.keys(WorkflowEngine).map((x) => (
                <MenuItem value={x}>{x}</MenuItem>
              ))}
            </FormInputSelect>
          </Grid>
        </Grid>
      </FormSection>
      <FormSection title={t("form:section.title.objectMatching")} status="todo" defaultExpanded sx={{ mt: 2 }}>
        <Grid container spacing={3} flexDirection={"row"}>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.objectTypesToMatch")}
              options={Object.keys(ObjectType)}
              placeholder={t("field:placeholder.selectObjectTypes")}
              value={wf?.filter?.objectTypes ?? []}
              multiple
              onChange={(e) => {
                setWF(
                  produce((draft) => {
                    if (!draft.filter) draft.filter = new DbWorkflowDefinitionInputFilterInfos();
                    draft.filter.objectTypes = e;
                  })
                );
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "objtypes",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.objectLayersToMatch")}
              options={Object.keys(ObjectType)}
              placeholder={t("field:placeholder.selectObjectLayers")}
              value={wf?.filter?.objectLayers ?? []}
              multiple
              onChange={(e) => {
                setWF(
                  produce((draft) => {
                    if (!draft.filter) draft.filter = new DbWorkflowDefinitionInputFilterInfos();
                    draft.filter.objectLayers = e;
                  })
                );
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "objly",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.objectClassesToMatch")}
              options={Object.keys(ObjectClass)}
              placeholder={t("field:placeholder.selectObjectClass")}
              value={wf?.filter?.objectClasses ?? []}
              multiple
              onChange={(e) => {
                setWF(
                  produce((draft) => {
                    if (!draft.filter) draft.filter = new DbWorkflowDefinitionInputFilterInfos();
                    draft.filter.objectClasses = e;
                  })
                );
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "objclass",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <FormAutocomplete
              label={t("field:label.objectStatusesToMatch")}
              options={Object.keys(ObjectStatus)}
              placeholder={t("field:placeholder.selectObjectStatus")}
              value={wf?.filter?.objectStatuses ?? []}
              multiple
              onChange={(e) => {
                setWF(
                  produce((draft) => {
                    if (!draft.filter) draft.filter = new DbWorkflowDefinitionInputFilterInfos();
                    draft.filter.objectStatuses = e;
                  })
                );
              }}
              getOptionLabel={(value: any) => value}
              isOptionEqualToValue={(option, value) => option === value}
              fullWidth
              validationProps={{
                formId: "formatvalidation",
                formSectionId: "defs",
                fieldId: "objsts",
                required: false,
                initialState: false,
              }}
            />
          </Grid>
          <Grid item md={6}>
            <InputLabel sx={{ mb: 1 }}>{t("field:label.fileCategory")}</InputLabel>
            <Select
              value={wf?.filter?.fileCategory ?? "none"}
              sx={{ width: "100%" }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
              fullWidth
              onChange={(e) => {
                setWF(
                  produce((draft) => {
                    if (!draft.filter) draft.filter = new DbWorkflowDefinitionInputFilterInfos();
                    draft.filter.fileCategory =
                      e.target.value && e.target.value !== "none" ? FileCategory[e.target.value] : null;
                  })
                );
              }}
            >
              <MenuItem value={"none"} key={"none"}>
                {t("menu:item.noneHyphen")}
              </MenuItem>
              {Object.keys(FileCategory).map((type) => (
                <MenuItem value={type} key={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </FormSection>
    </Modal>
  );
});

export default ModalWorkflowModelEdit;
